import { forwardRef, useState, type ComponentRef } from "react";

import { useSafeAreaInsets, useWindowDimensions } from "@luce/core/hooks";

import { Chip } from "../../atoms/chip";
import {
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownRoot,
  DropdownTrigger,
} from "../../atoms/dropdown";
import { borderWidth, lineHeight, spacing, touchTarget } from "../../tokens";
import type { FilterDropdownProps } from "./types";
import { useCoarsePointer } from "./use-coarse-pointer";

// The menu's padding (`p-spacing-xs`) and border, above and below its items
const MENU_FRAME = 2 * (spacing.xs + borderWidth[1]);

/** The open list shows at most five options, then scrolls (Ardhi, DQ10). */
const FILTER_DROPDOWN_MAX_ROWS = 5;

// A menu row: the touch target on touch screens and native; with a mouse, its
// `py-spacing-sm` and the label's 20 line
const ROW_HEIGHT = { coarse: touchTarget, fine: 2 * spacing.sm + lineHeight.body.md };

// The ref is the trigger's, as the Dropdown atom's is (ADR-007)
const FilterDropdown = forwardRef<ComponentRef<typeof DropdownTrigger>, FilterDropdownProps>(
  (
    {
      options,
      value,
      defaultValue,
      onValueChange,
      placeholder = "Filter",
      size = "md",
      disabled = false,
      className,
      testID,
      accessibilityLabel,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue ?? "");
    const { height } = useWindowDimensions();
    const insets = useSafeAreaInsets();
    const rowHeight = ROW_HEIGHT[useCoarsePointer() ? "coarse" : "fine"];

    // The mode is decided on every render, as RadioGroup's (J1): `value`
    // given, even "" (no selection), is controlled; `undefined` is uncontrolled
    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;

    const selectedLabel = options.find((o) => o.value === currentValue)?.label;
    const hasSelection = Boolean(currentValue && selectedLabel);

    const handleValueChange = (newValue: string) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    };

    return (
      <DropdownRoot open={open} onOpenChange={setOpen}>
        {/* The trigger is a Chip (Chip #1): its sizes, desktop step, tap area and
            states. It keeps Chip's default look open or with a value, and only
            the caret flips (Ardhi, DQ9); it's a menu button, not a toggle, and
            plays no haptic, as before. A caller's name replaces the visible
            text, so it carries the value too. */}
        <DropdownTrigger
          ref={ref}
          asChild
          disabled={disabled}
          testID={testID}
          accessibilityLabel={
            accessibilityLabel && hasSelection
              ? `${accessibilityLabel}, ${selectedLabel}`
              : accessibilityLabel
          }
        >
          <Chip
            size={size}
            disabled={disabled}
            trailingIcon={open ? "CaretUp" : "CaretDown"}
            accessibilityState={{ expanded: open }}
            hapticFeedback="none"
            className={className}
          >
            {selectedLabel ?? placeholder}
          </Chip>
        </DropdownTrigger>
        {/* A long list scrolls after five rows, in a menu that fits the
            screen. iOS and Android move a tall menu up to stay on screen, so
            the screen's limit is the window less its safe area; on web it's
            the room on the side it opens. */}
        <DropdownContent
          scrollHeight={Math.max(
            0,
            Math.min(
              FILTER_DROPDOWN_MAX_ROWS * rowHeight,
              height - insets.top - insets.bottom - MENU_FRAME,
            ),
          )}
          className="web:flex web:flex-col web:max-h-[var(--radix-dropdown-menu-content-available-height)]"
        >
          {/* The options are radio items, so the current value is marked with
              the Dropdown page's leading dot and reported checked (S35). Every
              row keeps the same leading space; a pick closes the menu */}
          <DropdownRadioGroup value={currentValue} onValueChange={handleValueChange}>
            {options.map((option) => (
              <DropdownRadioItem
                key={option.value}
                label={option.label}
                value={option.value}
                disabled={option.disabled}
              />
            ))}
          </DropdownRadioGroup>
        </DropdownContent>
      </DropdownRoot>
    );
  },
);

FilterDropdown.displayName = "FilterDropdown";

export { FilterDropdown };
